import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Mic, MicOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { Agent } from '../../types';

interface GeminiVoiceChatProps {
  agent: Agent;
  sessionId: string;
  /** Compact mode — smaller UI for inline embedding */
  compact?: boolean;
  /** Called when the agent sends a text transcript */
  onAgentTranscript?: (text: string) => void;
  /** Called when user speech is detected (transcript if available) */
  onUserTranscript?: (text: string) => void;
  /** Called when a voice turn completes */
  onTurnComplete?: () => void;
  /** Called when voice status changes */
  onStatusChange?: (status: string) => void;
  /** Called when connection state changes */
  onConnectionChange?: (connected: boolean) => void;
  /** Prior conversation context (e.g. from text chat) to seed the voice session so it continues aware of what was discussed */
  initialContext?: string;
}

const GeminiVoiceChat: React.FC<GeminiVoiceChatProps> = ({
  agent, sessionId, compact,
  onAgentTranscript, onUserTranscript, onTurnComplete, onStatusChange, onConnectionChange,
  initialContext,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [status, setStatus] = useState('Initializing...');
  const [shouldAutoStart, setShouldAutoStart] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  // Holds a replacement socket being established in the background during a seamless reconnect
  const pendingWsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const audioQueueRef = useRef<ArrayBuffer[]>([]);
  const isPlayingRef = useRef(false);
  const isSpeakingRef = useRef(false);

  // Voice Activity Detection
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSpeechTimeRef = useRef<number>(Date.now());
  const hasDetectedSpeechRef = useRef(false);
  const SILENCE_THRESHOLD = 0.005;
  const SILENCE_DURATION = 2000;

  // Accumulate agent transcript chunks for the current turn
  const agentTranscriptRef = useRef('');
  
  // Session chaining — reconnect every 90s for continuous voice
  const sessionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const SESSION_DURATION = 110000; // ~1.83 min — reconnect before external ~2min limit (seamless swap)
  const isReconnectingRef = useRef(false);
  const conversationHistoryRef = useRef<string[]>([]);
  const hasHadActiveSessionRef = useRef(false);
  // Anchor: the opening of the conversation, always preserved so the agent never
  // "forgets the beginning" no matter how long the session runs across reconnects.
  const conversationAnchorRef = useRef<string[]>([]);
  const MAX_CONTEXT_ENTRIES = 40; // Keep last 40 exchanges so much more survives reconnect
  const MAX_ANCHOR_ENTRIES = 8;   // Preserve the first ~8 exchanges permanently

  const updateStatus = useCallback((s: string) => {
    setStatus(s);
    onStatusChange?.(s);
  }, [onStatusChange]);

  // Dynamic WebSocket URL — use API server for voice
  const getWsUrl = () => {
    const apiBase = process.env.REACT_APP_CORE_API || 'https://vidhiguruapi.vidhiguru.in';
    return apiBase.replace('http://', 'ws://').replace('https://', 'wss://') + '/live-voice';
  };

  useEffect(() => {
    // Seed conversation history from prior context (e.g. text chat) so voice continues aware of it
    if (initialContext && conversationHistoryRef.current.length === 0) {
      conversationHistoryRef.current = initialContext.split('\n').filter(Boolean).slice(-MAX_CONTEXT_ENTRIES * 2);
    }

    // Capture the opening of the conversation once, so it is never lost to the
    // rolling window when the session chains across the ~2-min limit.
    const captureAnchor = () => {
      if (conversationAnchorRef.current.length === 0 && conversationHistoryRef.current.length > 0) {
        conversationAnchorRef.current = conversationHistoryRef.current.slice(0, MAX_ANCHOR_ENTRIES);
      }
    };

    // Build the current conversation context string to hand to a (re)connecting session.
    // Always prepend the permanent anchor (start of conversation) + the recent rolling window.
    const buildContext = (): string => {
      captureAnchor();
      const recent = conversationHistoryRef.current.slice(-MAX_CONTEXT_ENTRIES);
      const anchor = conversationAnchorRef.current;
      // Avoid duplicating anchor lines that are still inside the recent window
      const anchorExtra = anchor.filter(a => !recent.includes(a));
      const parts = [...anchorExtra, ...recent];
      // On the very first connect, seed with initialContext (text-chat history) if present
      if (parts.length === 0 && initialContext) return initialContext.slice(0, 8000);
      const joined = parts.join('\n');
      return joined.length > 8000 ? joined.slice(-8000) : joined;
    };

    // Shared message handler — reads wsRef.current dynamically so it works across swaps.
    // `socket` is the socket this handler is bound to.
    const handleMessage = (socket: WebSocket) => async (event: MessageEvent) => {
      const data = JSON.parse(event.data);

      if (data.type === 'ready') {
        setIsConnected(true);
        onConnectionChange?.(true);

        // If this is the pending (background) socket during a planned swap, promote it now.
        if (isReconnectingRef.current && pendingWsRef.current === socket) {
          const oldWs = wsRef.current;
          wsRef.current = socket;           // atomically repoint audio to the new socket
          pendingWsRef.current = null;
          isReconnectingRef.current = false;
          // Close the old socket now that the new one is live (mic never stopped)
          if (oldWs && oldWs !== socket && oldWs.readyState === WebSocket.OPEN) {
            try { oldWs.close(); } catch { /* ignore */ }
          }
          console.log('🔄 Seamless session swap complete — mic uninterrupted');
          updateStatus(isActive ? 'Listening...' : 'Ready');
        } else {
          // Initial connection
          updateStatus('Ready — Starting hands-free mode...');
          if (!isReconnectingRef.current) {
            toast.success(`Connected to ${agent.name} voice!`);
          }
          isReconnectingRef.current = false;
          setShouldAutoStart(true);
        }

        // Schedule the next seamless reconnect for THIS socket
        if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
        sessionTimerRef.current = setTimeout(() => {
          console.log('🔄 Session chaining — opening replacement socket in background');
          openReplacementSocket();
        }, SESSION_DURATION);
        return;
      }

      // ── Handle response wrapper ──
      if (data.type === 'response') {
        if (data.responseType === 'audio' && data.audio) {
          try {
            const pcmData = base64ToArrayBuffer(data.audio);
            const wavData = pcmToWav(pcmData, 24000);
            audioQueueRef.current.push(wavData);
            if (!isPlayingRef.current) playAudioQueue();
          } catch (error) {
            console.error('❌ Error converting audio:', error);
          }
        }
        if (data.responseType === 'text' && data.text) {
          agentTranscriptRef.current += data.text;
          onAgentTranscript?.(data.text);
          conversationHistoryRef.current.push('Agent: ' + data.text);
          if (conversationHistoryRef.current.length > MAX_CONTEXT_ENTRIES * 2) {
            conversationHistoryRef.current = conversationHistoryRef.current.slice(-MAX_CONTEXT_ENTRIES);
          }
        }
        if (data.responseType === 'interrupted') {
          audioQueueRef.current = [];
          setIsSpeaking(false);
          updateStatus('Listening...');
        }
        if (data.responseType === 'turnComplete') {
          if (agentTranscriptRef.current) {
            const lastEntry = conversationHistoryRef.current[conversationHistoryRef.current.length - 1];
            if (!lastEntry || !agentTranscriptRef.current.startsWith(lastEntry.replace('Agent: ', ''))) {
              conversationHistoryRef.current.push('Agent: ' + agentTranscriptRef.current);
            }
          }
          agentTranscriptRef.current = '';
          onTurnComplete?.();
          updateStatus('Listening...');
        }
      }

      // ── Handle direct message types ──
      if (data.type === 'audio' && data.audio) {
        try {
          const pcmData = base64ToArrayBuffer(data.audio);
          const wavData = pcmToWav(pcmData, 24000);
          audioQueueRef.current.push(wavData);
          if (!isPlayingRef.current) playAudioQueue();
        } catch (error) {
          console.error('❌ Error converting audio:', error);
        }
      }
      if (data.type === 'text' && data.text) {
        agentTranscriptRef.current += data.text;
        onAgentTranscript?.(data.text);
        const trimmed = data.text.slice(0, 200);
        conversationHistoryRef.current.push('Agent: ' + trimmed);
        if (conversationHistoryRef.current.length > MAX_CONTEXT_ENTRIES * 2) {
          conversationHistoryRef.current = conversationHistoryRef.current.slice(-MAX_CONTEXT_ENTRIES);
        }
      }
      if (data.type === 'turnComplete') {
        if (agentTranscriptRef.current) {
          const lastEntry = conversationHistoryRef.current[conversationHistoryRef.current.length - 1];
          if (!lastEntry || !agentTranscriptRef.current.startsWith(lastEntry.replace('Agent: ', ''))) {
            conversationHistoryRef.current.push('Agent: ' + agentTranscriptRef.current);
          }
        }
        agentTranscriptRef.current = '';
        onTurnComplete?.();
        updateStatus('Listening...');
      }
      // ── Record USER speech into history so the agent remembers the user's side too ──
      // (Previously only Agent turns were kept, so half the conversation was lost on reconnect.)
      const userText =
        (data.type === 'user_transcript' || data.type === 'input_transcript' || data.type === 'transcript') ? data.text :
        (data.type === 'response' && data.responseType === 'userTranscript') ? data.text :
        undefined;
      if (userText && userText.trim()) {
        onUserTranscript?.(userText);
        conversationHistoryRef.current.push('User: ' + userText.trim().slice(0, 300));
        if (conversationHistoryRef.current.length > MAX_CONTEXT_ENTRIES * 2) {
          conversationHistoryRef.current = conversationHistoryRef.current.slice(-MAX_CONTEXT_ENTRIES * 2);
        }
      }

      if (data.type === 'error') {
        console.error('❌ Voice error:', data.message);
        // If a pending swap socket errors, discard it and keep the current one alive
        if (pendingWsRef.current === socket) {
          pendingWsRef.current = null;
          isReconnectingRef.current = false;
          try { socket.close(); } catch { /* ignore */ }
          console.warn('⚠️ Replacement socket errored — keeping current session');
          return;
        }
        updateStatus('Voice session ended — click mic to start again');
      }
    };

    const handleClose = (socket: WebSocket) => () => {
      // If a background (pending) socket closed before promotion, just discard it.
      if (pendingWsRef.current === socket) {
        pendingWsRef.current = null;
        isReconnectingRef.current = false;
        console.warn('⚠️ Replacement socket closed before ready — keeping current session');
        return;
      }
      // If this socket is no longer the active one (already swapped out), ignore its close.
      if (wsRef.current && wsRef.current !== socket) return;

      setIsConnected(false);
      onConnectionChange?.(false);
      if (sessionTimerRef.current) { clearTimeout(sessionTimerRef.current); sessionTimerRef.current = null; }

      // Genuine/unexpected disconnect of the active socket
      stopHandsFree();
      if (hasHadActiveSessionRef.current) {
        updateStatus('Voice session ended — click mic to start again');
      } else {
        // Never spoke — silently try to reconnect once
        updateStatus('Connecting...');
        setTimeout(() => {
          if (wsRef.current?.readyState === WebSocket.CLOSED || !wsRef.current) {
            connect(sessionId + '-' + Date.now(), buildContext());
          }
        }, 2000);
      }
    };

    // Create a socket, wire handlers, and set it as the active socket.
    const connect = (sid: string, context?: string) => {
      const socket = new WebSocket(getWsUrl());
      wsRef.current = socket;
      socket.onopen = () => {
        console.log('🎤 Connected to Gemini Live API');
        updateStatus('Connected');
        socket.send(JSON.stringify({ type: 'setup', sessionId: sid, agentId: agent.id, conversationContext: context || undefined }));
      };
      socket.onmessage = handleMessage(socket);
      socket.onerror = () => { updateStatus('Connection issue'); };
      socket.onclose = handleClose(socket);
      return socket;
    };

    // Open a replacement socket in the BACKGROUND without touching the mic or the current socket.
    // It gets promoted to active in handleMessage when it reports `ready`.
    const openReplacementSocket = () => {
      // Don't stack multiple pending sockets
      if (pendingWsRef.current) return;
      isReconnectingRef.current = true;
      updateStatus(isActive ? 'Listening...' : 'Refreshing session...');
      const context = buildContext();
      const socket = new WebSocket(getWsUrl());
      pendingWsRef.current = socket;
      socket.onopen = () => {
        socket.send(JSON.stringify({
          type: 'setup',
          sessionId: sessionId + '-' + Date.now(),
          agentId: agent.id,
          conversationContext: context || undefined,
        }));
      };
      socket.onmessage = handleMessage(socket);
      socket.onerror = () => {
        if (pendingWsRef.current === socket) {
          pendingWsRef.current = null;
          isReconnectingRef.current = false;
          console.warn('⚠️ Replacement socket connection error — keeping current session');
        }
      };
      socket.onclose = handleClose(socket);
    };

    connect(sessionId);

    return () => {
      stopHandsFree();
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
      if (pendingWsRef.current?.readyState === WebSocket.OPEN) { try { pendingWsRef.current.close(); } catch { /* ignore */ } }
      pendingWsRef.current = null;
      if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.close();
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, [agent.id, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (shouldAutoStart && isConnected && !isActive) {
      startHandsFree();
      setShouldAutoStart(false);
    }
  }, [shouldAutoStart, isConnected, isActive]);

  const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
    const bin = atob(base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
  };

  const pcmToWav = (pcmData: ArrayBuffer, sampleRate = 24000): ArrayBuffer => {
    const pcmView = new Int16Array(pcmData);
    const buf = new ArrayBuffer(44 + pcmData.byteLength);
    const v = new DataView(buf);
    const w = (o: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
    w(0, 'RIFF'); v.setUint32(4, 36 + pcmData.byteLength, true);
    w(8, 'WAVE'); w(12, 'fmt ');
    v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
    v.setUint32(24, sampleRate, true); v.setUint32(28, sampleRate * 2, true);
    v.setUint16(32, 2, true); v.setUint16(34, 16, true);
    w(36, 'data'); v.setUint32(40, pcmData.byteLength, true);
    new Int16Array(buf, 44).set(pcmView);
    return buf;
  };

  const playAudioQueue = async () => {
    if (isPlayingRef.current || audioQueueRef.current.length === 0) return;
    isPlayingRef.current = true;
    isSpeakingRef.current = true;
    setIsSpeaking(true);
    updateStatus('Agent speaking...');

    try {
      if (!audioContextRef.current) audioContextRef.current = new AudioContext({ sampleRate: 24000 });
      while (audioQueueRef.current.length > 0) {
        const audioData = audioQueueRef.current.shift();
        if (!audioData) continue;
        try {
          const audioBuffer = await audioContextRef.current.decodeAudioData(audioData.slice(0));
          const source = audioContextRef.current.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(audioContextRef.current.destination);
          await new Promise<void>((resolve) => {
            source.onended = () => resolve();
            try { source.start(0); } catch { resolve(); }
          });
        } catch { continue; }
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Audio playback error: ' + msg);
    } finally {
      isPlayingRef.current = false;
      isSpeakingRef.current = false;
      setIsSpeaking(false);
      updateStatus('Listening...');
    }
  };

  const detectVoiceActivity = (audioData: Float32Array): boolean => {
    let sum = 0;
    for (let i = 0; i < audioData.length; i++) sum += audioData[i] * audioData[i];
    return Math.sqrt(sum / audioData.length) > SILENCE_THRESHOLD;
  };

  const startHandsFree = async () => {
    if (!isConnected || isActive) return;

    try {
      setIsActive(true);
      updateStatus('Starting microphone...');
      hasDetectedSpeechRef.current = false;
      lastSpeechTimeRef.current = Date.now();

      const audioContext = new AudioContext({ sampleRate: 16000 });
      audioContextRef.current = audioContext;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      mediaStreamRef.current = stream;

      const source = audioContext.createMediaStreamSource(stream);
      const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);
      scriptProcessorRef.current = scriptProcessor;

      scriptProcessor.onaudioprocess = (event) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        if (isSpeakingRef.current) return;

        const inputData = event.inputBuffer.getChannelData(0);
        const speaking = detectVoiceActivity(inputData);

        if (speaking) {
          if (!hasDetectedSpeechRef.current) hasDetectedSpeechRef.current = true;
          setIsUserSpeaking(true);
          hasHadActiveSessionRef.current = true; // User has actually spoken
          lastSpeechTimeRef.current = Date.now();
          if (silenceTimeoutRef.current) { clearTimeout(silenceTimeoutRef.current); silenceTimeoutRef.current = null; }
        } else if (hasDetectedSpeechRef.current) {
          const timeSince = Date.now() - lastSpeechTimeRef.current;
          if (isUserSpeaking && timeSince > SILENCE_DURATION) {
            setIsUserSpeaking(false);
            hasDetectedSpeechRef.current = false;
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: 'audioStreamEnd' }));
            }
          }
        }

        // Convert & send PCM
        const pcmData = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        const uint8 = new Uint8Array(pcmData.buffer);
        let binary = '';
        for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
        const audioBase64 = btoa(binary);

        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'audio', audio: audioBase64, mimeType: 'audio/pcm;rate=16000' }));
        }
      };

      source.connect(scriptProcessor);
      scriptProcessor.connect(audioContext.destination);
      updateStatus('Listening... Speak naturally');
      toast.success('Hands-free mode activated!');
    } catch {
      toast.error('Microphone access denied');
      setIsActive(false);
    }
  };

  const stopHandsFree = () => {
    setIsActive(false);
    setIsUserSpeaking(false);
    updateStatus('Voice stopped');
    if (silenceTimeoutRef.current) { clearTimeout(silenceTimeoutRef.current); silenceTimeoutRef.current = null; }
    if (scriptProcessorRef.current) { scriptProcessorRef.current.disconnect(); scriptProcessorRef.current = null; }
    if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach(t => t.stop()); mediaStreamRef.current = null; }
  };

  // ── Compact mode: small inline bar ──
  if (compact) {
    const sessionEnded = hasHadActiveSessionRef.current && !isConnected && !isActive && status === 'Voice session ended — click mic to start again';

    // Session ended — show prominent restart prompt
    if (sessionEnded) {
      return (
        <div className="flex items-center gap-4 px-5 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b-2 border-amber-300 animate-pulse">
          <div className="text-3xl">⚠️</div>
          <div className="flex-1">
            <div className="text-base font-bold text-amber-900">Voice Session Ended</div>
            <div className="text-sm text-amber-700">Click the microphone button to restart voice conversation</div>
          </div>
          <button
            onClick={() => {
              // Trigger full reconnect by re-mounting
              const newWs = new WebSocket(getWsUrl());
              wsRef.current = newWs;
              newWs.onopen = () => {
                updateStatus('Connected');
                newWs.send(JSON.stringify({ type: 'setup', sessionId: sessionId + '-' + Date.now(), agentId: agent.id }));
              };
              newWs.onmessage = (event) => {
                const d = JSON.parse(event.data);
                if (d.type === 'ready') { setIsConnected(true); updateStatus('Ready'); setShouldAutoStart(true); }
              };
              newWs.onerror = () => updateStatus('Connection failed');
              newWs.onclose = () => { setIsConnected(false); updateStatus('Voice session ended — click mic to start again'); };
            }}
            className="px-5 py-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 text-white font-bold text-sm shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
          >
            <Mic className="w-5 h-5" /> Restart Voice
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
        {/* Status emoji */}
        <div className="text-2xl">
          {isUserSpeaking ? '🗣️' : isSpeaking ? '🔊' : isActive ? '👂' : isConnected ? '🎤' : '⏳'}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-800">Voice Mode</span>
            {isActive && (
              <span className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${isUserSpeaking ? 'bg-green-500 animate-pulse' : isSpeaking ? 'bg-blue-500 animate-pulse' : 'bg-gray-300'}`} />
                <span className="text-[10px] text-gray-500">
                  {isUserSpeaking ? 'You are speaking' : isSpeaking ? 'Agent speaking' : 'Listening...'}
                </span>
              </span>
            )}
          </div>
          <p className="text-[10px] text-gray-500 truncate">{status}</p>
        </div>

        {/* Waveform indicator */}
        {isActive && (
          <div className="flex items-center gap-0.5 h-6">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className={`w-1 rounded-full transition-all duration-150 ${
                  isUserSpeaking
                    ? 'bg-green-500 animate-pulse'
                    : isSpeaking
                    ? 'bg-blue-500 animate-pulse'
                    : 'bg-gray-300'
                }`}
                style={{
                  height: isUserSpeaking || isSpeaking ? `${12 + Math.random() * 12}px` : '4px',
                  animationDelay: `${i * 100}ms`,
                }}
              />
            ))}
          </div>
        )}

        {/* Control button */}
        {!isActive ? (
          <button
            onClick={startHandsFree}
            disabled={!isConnected || isSpeaking}
            className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <Mic className="w-5 h-5" />
          </button>
        ) : (
          <button
            onClick={stopHandsFree}
            className="w-10 h-10 rounded-full bg-red-500 text-white shadow-md hover:shadow-lg transition-all flex items-center justify-center"
          >
            <MicOff className="w-5 h-5" />
          </button>
        )}
      </div>
    );
  }

  // ── Full mode (original) ──
  return (
    <div className="flex flex-col items-center justify-center p-8">
      <div className="mb-8 text-center">
        <div className="text-6xl mb-4">
          {isUserSpeaking ? '🗣️' : isSpeaking ? '🔊' : isActive ? '👂' : isConnected ? '⚖️' : '⏳'}
        </div>
        <h3 className="text-2xl font-bold mb-2">{agent.name}</h3>
        <p className="text-gray-600">{status}</p>
        <p className="text-xs text-gray-500 mt-2">Vidhiguru Voice for Justice</p>
      </div>

      <div className="mb-6">
        {!isActive ? (
          <button
            onClick={startHandsFree}
            disabled={!isConnected || isSpeaking}
            className="w-32 h-32 rounded-full bg-gradient-to-br from-primary to-secondary text-white shadow-2xl hover:shadow-3xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <Mic className="w-16 h-16" />
          </button>
        ) : (
          <button
            onClick={stopHandsFree}
            className="w-32 h-32 rounded-full bg-red-500 text-white shadow-2xl hover:shadow-3xl transition-all flex items-center justify-center"
          >
            <MicOff className="w-16 h-16" />
          </button>
        )}
      </div>

      {isActive && (
        <div className="mb-4 flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${isUserSpeaking ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
          <span className="text-sm text-gray-600">
            {isUserSpeaking ? 'You are speaking...' : 'Waiting for speech...'}
          </span>
        </div>
      )}

      <div className="text-center text-sm text-gray-600 max-w-md">
        {isConnected ? (
          <>
            <p className="mb-2">
              {isActive ? 'Speak naturally — the agent responds when you pause' : 'Click the microphone to start'}
            </p>
            <p className="text-xs">Voice Activity Detection • Automatic turn-taking</p>
          </>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span>Connecting to Gemini Live API...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeminiVoiceChat;
